import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../Redux/authSlice";
import Loader from "../../component/Loader/Loader";
import successIcon from "./Assets/icon.png";

const FEATURES = [
  "Unlimited words per week",
  "Unlimited audio transcription per week",
  "Custom prompt creation",
  "Early access to new features",
];

const CheckIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    className="shrink-0"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="11" fill="#34C759" fillOpacity="0.15" />
    <path
      d="M7.5 12.4l3 3L16.5 9"
      stroke="#34C759"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);
  const email = user?.email || user?.EmailId || "";

  // Brief loading state before revealing the success page.
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Only reachable right after a successful checkout. Pasting the URL directly
  // (or any other entry) has no `fromCheckout` state, so we bounce the user.
  if (!location.state?.fromCheckout) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return <Loader />;
  }

  const handleGoToApp = () => {
    navigate("/app");
  };

  return (
    <div className="bg-white md:p-4 p-3 min-h-screen flex flex-col font-inter">
      <div className="relative rounded-2xl overflow-hidden border border-[#3AB1F626] flex-1 flex flex-col bg-[#EAF6FE]">
        {/* Base gradient tint */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#F4FAFF] via-[#E5F3FE] to-[#D8EFFD] pointer-events-none" />

        {/* Floating colour animation — same orbit motion as Home / Login */}
        <div className="absolute top-0 left-0 w-full h-full z-0 pointer-events-none">
          <svg
            className="absolute top-0 left-0 w-full h-full"
            viewBox="0 0 1000 1350"
            preserveAspectRatio="none"
          >
            <path
              id="paySuccessOrbitPath"
              d="M40,0 H960 A40,40 0 0 1 1000,40 V1310 A40,40 0 0 1 960,1350 H40 A40,40 0 0 1 0,1310 V40 A40,40 0 0 1 40,0 Z"
              fill="none"
              stroke="transparent"
            />
            <circle r="320" fill="url(#paySuccessGlow)" filter="url(#paySuccessBlur)">
              <animateMotion repeatCount="indefinite" dur="22s" rotate="auto">
                <mpath href="#paySuccessOrbitPath" />
              </animateMotion>
            </circle>
            <circle r="320" fill="url(#paySuccessGlow)" filter="url(#paySuccessBlur)">
              <animateMotion
                repeatCount="indefinite"
                dur="22s"
                rotate="auto"
                keyPoints="1;0"
                keyTimes="0;1"
              >
                <mpath href="#paySuccessOrbitPath" />
              </animateMotion>
            </circle>
            <defs>
              <radialGradient id="paySuccessGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#3AB1F6" stopOpacity=".45" />
                <stop offset="100%" stopColor="#3AB1F6" stopOpacity="0" />
              </radialGradient>
            </defs>
            <filter
              id="paySuccessBlur"
              x="-100%"
              y="-100%"
              width="300%"
              height="300%"
            >
              <feGaussianBlur in="SourceGraphic" stdDeviation="22" />
            </filter>
          </svg>
        </div>

        {/* Logged in as — top left */}
        {email && (
          <div className="relative z-10 p-5 sm:p-6">
            <p className="text-[11px] sm:text-[12px] text-[#00000059] leading-tight">
              Logged in as
            </p>
            <p className="text-[12px] sm:text-[13px] text-[#000000B3] font-medium leading-tight mt-0.5">
              {email}
            </p>
          </div>
        )}

        {/* Centered content — sits slightly above center */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 sm:px-6 text-center pt-8 sm:pt-12 pb-28 sm:pb-32">
          <img
            src={successIcon}
            alt="Success"
            className="w-14 h-14 sm:w-[60px] sm:h-[60px] mb-5"
          />

          <h1 className="font-bold text-[#111111] text-[22px] sm:text-[26px] md:text-[30px] leading-[1.25] tracking-[-0.01em]">
            You've successfully upgraded
            <br />
            to ZT Pro!
          </h1>

          <p className="text-[#00000080] text-[12px] sm:text-[13px] mt-3 mb-5">
            With the Pro plan you get:
          </p>

          {/* Feature grid 2x2 */}
          <div className="grid grid-cols-1 sm:grid-cols-[auto_auto] justify-center gap-x-8 gap-y-3 mb-8 text-left">
            {FEATURES.map((feature) => (
              <div key={feature} className="flex items-center gap-2.5">
                <CheckIcon />
                <span className="text-black font-bold text-[13px] sm:text-[14px] whitespace-nowrap">
                  {feature}
                </span>
              </div>
            ))}
          </div>

          {/* Go to the app */}
          <button
            type="button"
            onClick={handleGoToApp}
            style={{
              background: "linear-gradient(90deg, #3AB1F6 0%, #407BFF 100%)",
            }}
            className="text-white text-[14px] sm:text-[15px] font-semibold rounded-full px-7 py-2.5 shadow-md hover:opacity-90 transition-opacity duration-300"
          >
            Go to the app
          </button>

          {/* Download links */}
          <p className="text-[#00000080] text-[11px] sm:text-[12px] mt-5">
            or download Zero Typing AI for{" "}
            <span className="text-[#3AB1F6] font-medium underline cursor-pointer">
              Apple Silicon Mac
            </span>{" "}
            or{" "}
            <span className="text-[#3AB1F6] font-medium underline cursor-pointer">
              Intel Mac
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
